import React, { useEffect, useState } from 'react';

import { useTrialsContext } from 'contexts/TrialsContextProvider';
import { hasValidFields } from 'lib/fieldValidation';
import useChangePage from 'hooks/useChangePage';
import useTrackAmplitudeEvent from 'hooks/useTrackAmplitudeEvent';
import useQueryParams from 'hooks/useQueryParams';

import MobileField from '../../components/FormFields/MobileField';
import NameField from '../../components/FormFields/NameField';
import { VERIFY_ACCOUNT_MODAL_ROUTE } from '../../constants';
import useTrialProtectedPageGuard from '../../hooks/useTrialProtectedPageGuard';
import { FakeWrapper } from '../../layout';
import { Subheading } from '../../styles';

const Profile = () => {
  const changePage = useChangePage();
  const trackEvent = useTrackAmplitudeEvent('trials.profile.visited');
  const {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    phone,
    hasValidName,
    setLoginInfoErrors,
    setNameErrors,
    accountExists,
    checkPhoneNumberExists,
  } = useTrialsContext() || {};

  useTrialProtectedPageGuard();

  const { value: updatePhoneValue } = useQueryParams('updatePhone');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setNameErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateProfile = async () => {
    const validName = hasValidName(true);
    const validPhone = hasValidFields({ phone }, setLoginInfoErrors);

    if (!validName)
      trackEvent('trials.profile.error', {
        error: 'invalid name',
        firstName,
        lastName,
      });

    if (!validPhone) {
      trackEvent('trials.profile.error', { error: 'invalid phone', phone });
    } else {
      const phoneExists = await checkPhoneNumberExists(phone);
      return validName && validPhone && !phoneExists;
    }

    return validName && validPhone;
  };

  const handleRedirect = () => {
    changePage(`/trials/${VERIFY_ACCOUNT_MODAL_ROUTE}`, true);
  };

  const handleSubmit = () =>
    validateProfile()
      .then((isValidProfile) => {
        setIsSubmitting(false);

        if (isValidProfile && !accountExists) {
          trackEvent('trials.profile.nextClicked');
          handleRedirect();
        }
      })
      .catch((error) => {
        trackEvent('trials.profile.error', { error: JSON.stringify(error) });
        setIsSubmitting(false);
      });

  const modalTitle = updatePhoneValue
    ? 'Update your mobile number'
    : 'Let’s set up your profile.';

  return (
    <FakeWrapper
      handleButtonClick={handleSubmit}
      title={modalTitle}
      ctaId="set-up-profile-continue-btn"
    >
      <Subheading>Start by adding your name and phone number.</Subheading>

      <NameField
        disabled={isSubmitting}
        fieldName="firstName"
        placeholder="First Name"
        setFieldValue={setFirstName}
        value={firstName}
      />

      <NameField
        disabled={isSubmitting}
        fieldName="lastName"
        placeholder="Last Name"
        setFieldValue={setLastName}
        value={lastName}
      />

      <MobileField disabled={isSubmitting} />
    </FakeWrapper>
  );
};

export default Profile;
